import { useState } from "react";
import { useDispatch } from "react-redux";
import { HiOutlineTrash, HiOutlineMail, HiOutlineRefresh } from "react-icons/hi";
import Badge from "../shared/Badge";
import {
  revokeInvitationAction,
  resendInvitationAction,
} from "../../redux/actions/usersActions";

const STATUS_BADGE = {
  pending: "info",
  accepted: "success",
  revoked: "danger",
  expired: "warning",
};

const PendingInvitations = ({ invitations, organisationId }) => {
  const dispatch = useDispatch();
  const [revoking, setRevoking] = useState(null);
  const [resending, setResending] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const handleRevoke = async (invitationId) => {
    setRevoking(invitationId);
    setError("");
    setNotice("");
    try {
      await dispatch(revokeInvitationAction(organisationId, invitationId));
    } catch (err) {
      setError(err.message || "Failed to revoke invitation");
    } finally {
      setRevoking(null);
    }
  };

  const handleResend = async (invitation) => {
    setResending(invitation._id);
    setError("");
    setNotice("");
    try {
      const data = await dispatch(
        resendInvitationAction(organisationId, invitation._id)
      );
      if (data?.emailSent === false) {
        const detail = data?.emailError ? ` (${data.emailError})` : "";
        setError(`Could not deliver email to ${invitation.email}${detail}.`);
      } else {
        setNotice(`Invitation email re-sent to ${invitation.email}.`);
      }
    } catch (err) {
      setError(err.message || "Failed to resend invitation");
    } finally {
      setResending(null);
    }
  };

  if (invitations.length === 0) {
    return (
      <div className="text-center py-8 text-muted text-body-md">
        No pending invitations.
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 p-3 rounded-md border border-signature-coral bg-signature-coral/5">
          <p className="text-body-md text-signature-coral">{error}</p>
        </div>
      )}
      {notice && (
        <div className="mb-4 p-3 rounded-md border border-success bg-success/5">
          <p className="text-body-md text-ink">{notice}</p>
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-hairline bg-canvas">
        <div className="overflow-x-auto">
          <table className="w-full text-body-md">
            <thead>
              <tr className="text-[12px] uppercase tracking-wide bg-surface-soft text-ink border-b border-hairline">
                <th className="px-6 py-4 text-left font-medium">Email</th>
                <th className="px-6 py-4 text-left font-medium">Role</th>
                <th className="px-6 py-4 text-left font-medium">Status</th>
                <th className="px-6 py-4 text-left font-medium">Expires</th>
                <th className="px-6 py-4 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {invitations.map((inv) => (
                <tr key={inv._id} className="hover:bg-surface-soft">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <HiOutlineMail className="h-4 w-4 text-muted shrink-0" />
                      <span className="text-ink">{inv.email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-body text-muted capitalize">
                      {inv.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={STATUS_BADGE[inv.status] ?? "default"}>
                      {inv.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-muted">
                    {inv.expiresAt
                      ? new Date(inv.expiresAt).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-6 py-4">
                    {inv.status === "pending" && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleResend(inv)}
                          disabled={resending === inv._id || revoking === inv._id}
                          title="Resend invitation email"
                          className="p-2 rounded-md text-muted hover:text-ink hover:bg-surface-soft transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <HiOutlineRefresh
                            className={`h-4 w-4 ${
                              resending === inv._id ? "animate-spin" : ""
                            }`}
                          />
                        </button>
                        <button
                          onClick={() => handleRevoke(inv._id)}
                          disabled={revoking === inv._id || resending === inv._id}
                          title="Revoke invitation"
                          className="p-2 rounded-md text-muted hover:text-signature-coral hover:bg-surface-soft transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <HiOutlineTrash className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PendingInvitations;
